import { Phone, Clock, Shield, Star, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const badges = [
  { icon: Clock, label: "24h/24 - 7j/7" },
  { icon: Shield, label: "Agréé CPAM" },
  { icon: Star, label: "Chauffeur expérimenté" },
];

export default function HeroSection() {
  return (
    <section className="hero-bg min-h-screen flex items-center relative overflow-hidden" data-testid="hero-section" aria-label="Accueil RSE TAXI">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold tracking-wider uppercase mb-6">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              Disponible maintenant
            </span>
          </motion.div>

          <motion.h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-title"
          >
            <span className="text-white">Taxi</span>
            <br />
            <span className="text-gold-gradient">Nord de Toulouse</span>
          </motion.h1>

          <motion.p
            className="font-tagline text-base md:text-lg font-semibold tracking-[0.3em] uppercase text-amber-400/80 mt-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            data-testid="hero-tagline"
          >
            Rapide &bull; Simple &bull; Efficace
          </motion.p>

          <motion.h2
            className="text-base md:text-lg text-zinc-400 mt-6 leading-relaxed max-w-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            Votre taxi conventionné CPAM à Launaguet. Transport médical VSL,
            courses professionnelles et privées pour tout le Nord de Toulouse.
          </motion.h2>

          <motion.div
            className="flex flex-wrap gap-3 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            {badges.map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-300 backdrop-blur-sm"
              >
                <b.icon className="h-4 w-4 text-amber-400" />
                {b.label}
              </span>
            ))}
          </motion.div>

          <motion.div
            className="flex flex-wrap gap-4 mt-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                size="lg"
                className="relative bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wider shadow-[0_0_30px_rgba(245,158,11,0.4)] hover:shadow-[0_0_50px_rgba(245,158,11,0.6)] transition-all duration-300 rounded-full px-10 h-14 text-base overflow-hidden group"
                onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-book-btn"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-amber-400/0 via-white/30 to-amber-400/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                <span className="relative">Réserver maintenant</span>
              </Button>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="relative"
            >
              <Button
                variant="outline"
                size="lg"
                className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/70 rounded-full px-10 h-14 backdrop-blur-sm font-bold tracking-wider shadow-[0_0_15px_rgba(245,158,11,0.15)] hover:shadow-[0_0_30px_rgba(245,158,11,0.3)] transition-all duration-300 overflow-hidden group"
                asChild
              >
                <a href="tel:0652620747" data-testid="hero-call-btn">
                  <span className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-amber-500/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                  <Phone className="h-5 w-5 mr-2 relative" />
                  <span className="relative">06 52 62 07 47</span>
                </a>
              </Button>
              {/* Pulse ring on phone button */}
              <span className="absolute inset-0 rounded-full border border-amber-500/20 animate-ping opacity-40 pointer-events-none" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
      >
        <ChevronDown className="h-6 w-6 text-amber-400/50" />
      </motion.div>
    </section>
  );
}
